import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, Tag, Typography, Space, message, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, KeyOutlined } from '@ant-design/icons';
import { User, UserLevel, CreateUserData, UpdateUserData } from '../types.ts';
import { userAPI, userLevelAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import ChangePasswordModal from '../components/ChangePasswordModal.tsx';

const { Title } = Typography;

const Users: React.FC = () => {
  // Pagina raggiungibile solo dall'amministratore (AdminOnlyRoute): nessun gating permessi
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [levels, setLevels] = useState<UserLevel[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [changingPasswordUser, setChangingPasswordUser] = useState<User | null>(null);
  const [form] = Form.useForm();

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await userAPI.getUsers();
      setUsers(data);
    } catch (error) {
      message.error('Errore nel caricamento utenti');
    } finally {
      setLoading(false);
    }
  };

  const fetchLevels = async () => {
    try {
      const data = await userLevelAPI.getLevels();
      setLevels(data);
    } catch (error) {
      message.error('Errore nel caricamento livelli utente');
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchLevels();
  }, []);

  const handleAdd = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({ level_id: 2 });
    setModalVisible(true);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    form.setFieldsValue({
      username: user.username,
      email: user.email,
      level_id: user.level_id || 2,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await userAPI.deleteUser(id);
      message.success('Utente eliminato con successo');
      fetchUsers();
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore nell\'eliminazione dell\'utente');
    }
  };

  const handleChangePassword = (user: User) => {
    setChangingPasswordUser(user);
    setPasswordModalVisible(true);
  };

  const handlePasswordModalClose = () => {
    setPasswordModalVisible(false);
    setChangingPasswordUser(null);
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingUser) {
        const updateData: UpdateUserData = {
          username: values.username,
          email: values.email,
          level_id: values.level_id,
        };
        await userAPI.updateUser(editingUser.id, updateData);
        message.success('Utente aggiornato con successo');
      } else {
        const createData: CreateUserData = {
          username: values.username,
          email: values.email,
          password: values.password,
          level_id: values.level_id,
        };
        await userAPI.createUser(createData);
        message.success('Utente creato con successo');
      }
      setModalVisible(false);
      fetchUsers();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Errore nell\'operazione';
      message.error(errorMessage);
    }
  };

  const getLevelColor = (levelId?: number) => {
    switch (levelId) {
      case 1: return 'red';
      case 2: return 'blue';
      case 3: return 'green';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Livello',
      key: 'level',
      width: 180,
      render: (_: any, record: User) => (
        <Tag color={getLevelColor(record.level_id)}>
          {record.level?.level_name || '-'}
        </Tag>
      ),
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 160,
      render: (_: any, record: User) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          {currentUser?.level_id === 1 && (
            <Button
              type="default"
              size="small"
              icon={<KeyOutlined />}
              onClick={() => handleChangePassword(record)}
              title="Cambia Password"
            />
          )}
          <Popconfirm
            title="Sei sicuro di voler eliminare questo utente?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sì"
            cancelText="No"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2}>Gestione Utenti</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Nuovo Utente
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingUser ? 'Modifica Utente' : 'Nuovo Utente'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        destroyOnClose
        className="no-uppercase-modal"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="username"
            label="Username"
            rules={[{ required: true, message: 'Username richiesto' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[{ type: 'email', message: 'Email non valida' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="level_id"
            label="Livello Utente"
            rules={[{ required: true, message: 'Seleziona un livello' }]}
          >
            <Select placeholder="Seleziona livello">
              {levels.map(level => (
                <Select.Option key={level.id} value={level.id}>
                  {level.level_name}{level.description ? ` - ${level.description}` : ''}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {!editingUser && (
            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: 'Password richiesta' },
                { min: 10, message: 'La password deve avere almeno 10 caratteri' },
                { pattern: /^(?=.*[A-Za-z])(?=.*\d).+$/, message: 'La password deve contenere almeno una lettera e una cifra' },
              ]}
              extra="Minimo 10 caratteri, con almeno una lettera e una cifra"
            >
              <Input.Password />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {changingPasswordUser && (
        <ChangePasswordModal
          visible={passwordModalVisible}
          onClose={handlePasswordModalClose}
          user={changingPasswordUser}
        />
      )}
    </div>
  );
};

export default Users;
